import clsx from 'clsx'
import React from 'react'
import { useSelector } from 'react-redux'
import { Button, Icon, Modal, RadioGroup, Switch } from '../../../components'
import { isDesktop } from '../../../utils'
import CustomToken from '../CustomToken'

import './Settings.scss'

const Settings = ({
   close,
   isLimitOrder,
   isPartialFill,
   setIsPartialFill,
   isCHIToken,
   setIsCHIToken,
   isOptimizedRouter,
   setIsOptimizedRouter,
   slippageTolerence,
   setSlippageTolerence,
   gasPrice,
   setGasPrice,
   customTokens,
   setCustomTokens,
}) => {
   const SLIPPAGE_OPTIONS = React.useMemo(
      () => [
         { id: 1, title: '0.1%' },
         { id: 2, title: '0.5%' },
         { id: 3, title: '1.0%' },
         { id: 4, title: '3.0%' },
      ],
      []
   )
   const GAS_PRICE_OPTIONS = React.useMemo(
      () => [
         { id: 1, title: '6 Standard' },
         { id: 2, title: '12 Fast' },
         { id: 3, title: '15 Instant' },
      ],
      []
   )

   const { isDarkTheme } = useSelector(state => state.theme)

   const [showPartialFillTooltip, setShowPartialFillTooltip] =
      React.useState(false)
   const [showCHITokenTooltip, setShowCHITokenTooltip] = React.useState(false)
   const [showRouterTooltip, setShowRouterTooltip] = React.useState(false)

   const [showTolerenceOptions, setShowTolerenceOptions] = React.useState(false)
   const [showGasPriceOptions, setShowGasPriceOptions] = React.useState(false)
   const [showCustomTokens, setShowCustomTokens] = React.useState(false)
   const [showTokenForm, setShowTokenForm] = React.useState(false)
   const [tokenAddress, setTokenAddress] = React.useState('')

   const reset = () => {
      setIsPartialFill(false)
      setIsCHIToken(false)
      setIsOptimizedRouter(false)
      setSlippageTolerence(SLIPPAGE_OPTIONS[0])
      setGasPrice(GAS_PRICE_OPTIONS[0])
      setCustomTokens([])
   }

   React.useEffect(() => {
      if (!slippageTolerence) {
         setSlippageTolerence(SLIPPAGE_OPTIONS[0])
      }
      if (!gasPrice) {
         setGasPrice(GAS_PRICE_OPTIONS[0])
      }
   }, [
      slippageTolerence,
      gasPrice,
      setSlippageTolerence,
      SLIPPAGE_OPTIONS,
      setGasPrice,
      GAS_PRICE_OPTIONS,
   ])

   return (
      <div
         className={clsx(
            'ExchangeSettings',
            isDarkTheme && 'ExchangeSettings--dark',
            isDesktop() && 'ExchangeSettings--desktop'
         )}
      >
         <div className="ExchangeSettings__header">
            <h4 className="ExchangeSettings__header-heading">
               Advanced Settings
            </h4>
            <Icon
               className="ExchangeSettings__header-close"
               name="close"
               onClick={close}
            />
         </div>
         {!isLimitOrder && (
            <>
               <div className="ExchangeSettings__setting">
                  <p className="ExchangeSettings__setting-key">
                     Partial Fill{' '}
                     <Icon
                        className="ExchangeSettings__setting-key-icon"
                        name="info"
                        size={16}
                        onMouseEnter={() => setShowPartialFillTooltip(true)}
                        onMouseLeave={() => setShowPartialFillTooltip(false)}
                     />
                     {showPartialFillTooltip && (
                        <span className="ExchangeSettings__setting-key-tooltip">
                           Partial fill applies only when swap routes has
                           several main route parts. It allows to partially
                           execute in order to prevent transaction revert when
                           market price gets worse
                        </span>
                     )}
                  </p>
                  <Switch
                     name="partial-fill"
                     checked={isPartialFill}
                     onChange={() => setIsPartialFill(!isPartialFill)}
                  />
               </div>
               <div className="ExchangeSettings__setting">
                  <p className="ExchangeSettings__setting-key">
                     CHI Token{' '}
                     <Icon
                        className="ExchangeSettings__setting-key-icon"
                        name="info"
                        size={16}
                        onMouseEnter={() => setShowCHITokenTooltip(true)}
                        onMouseLeave={() => setShowCHITokenTooltip(false)}
                     />
                     {showCHITokenTooltip && (
                        <span className="ExchangeSettings__setting-key-tooltip">
                           CHI token is a gas token, that helps to save tx gas
                           fees. To activate this option connect your wallet and
                           check that you have CHI on your balance. Also please
                           be aware that gas tokens might no longer work on
                           Ethereum network, after Ethereum London Hardfork
                        </span>
                     )}
                  </p>
                  <Switch
                     name="chi-token"
                     checked={isCHIToken}
                     onChange={() => setIsCHIToken(!isCHIToken)}
                  />
               </div>
               <div className="ExchangeSettings__setting">
                  <p className="ExchangeSettings__setting-key">
                     Optimized Router{' '}
                     <Icon
                        className="ExchangeSettings__setting-key-icon"
                        name="info"
                        size={16}
                        onMouseEnter={() => setShowRouterTooltip(true)}
                        onMouseLeave={() => setShowRouterTooltip(false)}
                     />
                     {showRouterTooltip && (
                        <span className="ExchangeSettings__setting-key-tooltip">
                           Disable this option in case you got a problem with a
                           token that charge internal commission on
                           swaps/transfers
                        </span>
                     )}
                  </p>
                  <Switch
                     name="optimized-router"
                     checked={isOptimizedRouter}
                     onChange={() => setIsOptimizedRouter(!isOptimizedRouter)}
                  />
               </div>
               <div className="ExchangeSettings__setting">
                  <p className="ExchangeSettings__setting-key">
                     Slippage Tolerence
                  </p>
                  <span
                     className="ExchangeSettings__setting-value"
                     onClick={() =>
                        setShowTolerenceOptions(!showTolerenceOptions)
                     }
                  >
                     {slippageTolerence?.title || 'NA'}
                     <Icon
                        className="ExchangeSettings__setting-value-icon"
                        name="chevron-down"
                     />
                  </span>
               </div>
               {showTolerenceOptions && (
                  <RadioGroup
                     className="ExchangeSettings__radio-group"
                     selectedOption={slippageTolerence}
                     setSelectedOption={op => {
                        setSlippageTolerence(op)
                        setShowTolerenceOptions(false)
                     }}
                     options={SLIPPAGE_OPTIONS}
                     isDarkTheme={isDarkTheme}
                  />
               )}
            </>
         )}
         <div className="ExchangeSettings__setting">
            <p className="ExchangeSettings__setting-key">Gas Price</p>
            <span
               className="ExchangeSettings__setting-value"
               onClick={() => setShowGasPriceOptions(!showGasPriceOptions)}
            >
               {gasPrice?.title || 'NA'}
               <Icon
                  className="ExchangeSettings__setting-value-icon"
                  name="chevron-down"
               />
            </span>
         </div>
         {showGasPriceOptions && (
            <RadioGroup
               className="ExchangeSettings__radio-group"
               selectedOption={gasPrice}
               setSelectedOption={op => {
                  setGasPrice(op)
                  setShowGasPriceOptions(false)
               }}
               options={GAS_PRICE_OPTIONS}
               isDarkTheme={isDarkTheme}
            />
         )}
         <div className="ExchangeSettings__setting">
            <p className="ExchangeSettings__setting-key">
               Custom Tokens{' '}
               <Icon
                  className="ExchangeSettings__setting-key-icon"
                  name="outline-plus"
                  onClick={() => setShowTokenForm(true)}
               />
            </p>
            <span
               className="ExchangeSettings__setting-value"
               onClick={() => setShowCustomTokens(!showCustomTokens)}
            >
               {customTokens.length}
               <Icon
                  className="ExchangeSettings__setting-value-icon"
                  name="chevron-down"
               />
            </span>
         </div>
         {showCustomTokens && (
            <div className="ExchangeSettings__list">
               {customTokens.map(token => (
                  <div className="ExchangeSettings__list-token" key={token.id}>
                     <img
                        className="ExchangeSettings__list-token-image"
                        src={token.image}
                        alt={token.name}
                     />
                     <span className="ExchangeSettings__list-token-name">
                        {token.name}
                     </span>
                     <span className="ExchangeSettings__list-token-value">
                        {token.value}
                        {token.unit}
                     </span>
                  </div>
               ))}
            </div>
         )}
         <div className="ExchangeSettings__cta">
            <Button
               variant="solid"
               color="primary"
               className="ExchangeSettings__cta-btn"
               onClick={reset}
               size={isDesktop() ? 'lg' : undefined}
            >
               Reset
            </Button>
            <Button
               variant="outline"
               color="primary"
               className="ExchangeSettings__cta-btn"
               onClick={close}
               size={isDesktop() ? 'lg' : undefined}
            >
               Close
            </Button>
            <Modal
               isOpen={showTokenForm}
               close={() => setShowTokenForm(false)}
               heading="Add Custom Token"
            >
               <CustomToken value={tokenAddress} setValue={setTokenAddress} />
            </Modal>
         </div>
      </div>
   )
}

export default Settings
